// Holding an app: its quick actions first (the one or two things you open it
// for), then what you can do with the icon itself (home screen, launcher,
// arranging). One sheet for the home screens, the launcher and the dock, so
// a hold means the same thing everywhere.
import { PrimeMark } from '@/components/PrimeMark'
import { useNavigate } from '@tanstack/react-router'
import type { LucideProps } from 'lucide-react'
import type { ComponentType } from 'react'
import {
  Armchair,
  CalendarPlus,
  UserPlus,
  CalendarClock,
  ClipboardCheck,
  Cigarette,
  EyeOff,
  FilePlus,
  LayoutGrid,
  ListChecks,
  MessageSquarePlus,
  Mic,
  Moon,
  Pause,
  Play,
  Plus,
  Sparkles,
  SquarePlus,
  SquareMinus,
  Bell,
  SunMoon,
} from 'lucide-react'
import { Sheet, cx } from '@/components/ui'
import { useAllLists, usePages, useSettings, useTasks } from '@/db/hooks'
import { useUi } from '@/state/ui'
import { useScope } from '@/state/scope'
import { usePomodoro } from '@/state/pomodoro'
import { useAgentState } from '@/voice/agent'
import { selectToday } from '@/lib/today'
import { todayLocal } from '@/lib/time'
import { taskActions } from '@/lib/actions'
import { eveningOf, logSmoke } from '@/lib/wellbeing'
import { createPage } from '@/kinds/page/components'
import { sortLists } from '@/kinds/list/components'
import type { NavItem } from '@/lib/nav'
import { addItem, hideApp, newKey, onHome, removeApp, type PhoneLayout } from './layout'
import { savePhone, useEditing, useLauncher } from './phone'
import { AppTile } from './AppIcon'

interface QuickAction {
  label: string
  Icon: ComponentType<LucideProps>
  run: () => void | Promise<unknown>
}

/** What an app offers when held. Only things that are one tap and obviously safe. */
const useQuickActions = (app: NavItem | null): QuickAction[] => {
  const navigate = useNavigate()
  const tasks = useTasks() ?? []
  const lists = useAllLists() ?? []
  const pages = usePages() ?? []
  const settings = useSettings()
  const scope = useScope((s) => s.ws)
  const pomodoro = usePomodoro()
  const agent = useAgentState()
  if (!app) return []
  const go = (to: string, hash?: string) => () => void navigate({ to, hash })
  const add = () => useUi.getState().setQuickAdd(true)
  switch (app.key) {
    case 'today': {
      const first = selectToday(tasks, {
        today: todayLocal(),
        cap: settings.today_cap,
        picker: { time: 'any', energy: 'any' },
        activeId: null,
      }).picked[0]
      return [
        ...(first
          ? [
              {
                label: `Start “${first.spec.title.length > 26 ? `${first.spec.title.slice(0, 24)}…` : first.spec.title}”`,
                Icon: Play,
                run: () => taskActions.start(first),
              },
            ]
          : []),
        { label: 'Add something', Icon: Plus, run: add },
      ]
    }
    case 'inbox':
      return [{ label: 'Add to the inbox', Icon: Plus, run: add }]
    case 'upcoming':
      return [{ label: 'No date yet', Icon: CalendarClock, run: go('/upcoming', 'nodate') }]
    case 'agents':
      return [
        { label: 'Ask Prime', Icon: PrimeMark, run: go('/agents') },
        {
          label: 'New chat',
          Icon: MessageSquarePlus,
          run: () => void navigate({ to: '/agents', search: { fresh: true } as never }),
        },
        ...(agent.available && agent.status === 'disconnected'
          ? [{ label: 'Talk', Icon: Mic, run: () => void agent.start().catch(() => {}) }]
          : []),
      ]
    case 'focus':
      return pomodoro.status === 'running'
        ? [{ label: 'Pause focus', Icon: Pause, run: () => pomodoro.pause() }]
        : [
            {
              label:
                pomodoro.status === 'paused' ? 'Resume focus' : `Start ${pomodoro.config.work} min focus`,
              Icon: Play,
              run: () => pomodoro.start(),
            },
          ]
    case 'lists':
      return sortLists(lists.filter((l) => !l.deleted_at && !l.status.converted_to))
        .slice(0, 4)
        .map((l) => ({ label: l.spec.title, Icon: ListChecks, run: go(`/lists/${l.id}`) }))
    case 'pages':
      return [
        {
          label: 'New page',
          Icon: FilePlus,
          run: async () => {
            const r = await createPage(scope, undefined, pages)
            void navigate({ to: '/pages/$id', params: { id: r.id } })
          },
        },
      ]
    case 'wellbeing':
      return [
        { label: 'Log a smoke', Icon: Cigarette, run: () => logSmoke(eveningOf(Date.now())) },
        { label: 'End of day', Icon: Moon, run: go('/wellbeing') },
        { label: 'The chair', Icon: Armchair, run: go('/reset') },
        { label: 'Check in', Icon: ClipboardCheck, run: go('/checkin') },
      ]
    case 'dates':
      return [{ label: 'Add a date', Icon: CalendarPlus, run: go('/dates/new') }]
    case 'people':
      return [{ label: 'Add someone', Icon: UserPlus, run: go('/people/new') }]
    case 'music':
      return [{ label: 'Insights', Icon: Sparkles, run: go('/music/insights') }]
    case 'settings':
      return [
        { label: 'Appearance', Icon: SunMoon, run: go('/settings/appearance') },
        { label: 'Home screen', Icon: LayoutGrid, run: go('/settings/home') },
        { label: 'Notifications', Icon: Bell, run: go('/settings/notifications') },
        { label: 'Voice', Icon: Mic, run: go('/settings/voice') },
      ]
    default:
      return []
  }
}

const Row = ({
  Icon,
  label,
  onClick,
  tone,
  testid,
}: {
  Icon: ComponentType<LucideProps>
  label: string
  onClick: () => void
  tone?: 'danger'
  testid?: string
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cx(
      'w-full flex items-center gap-3 px-3.5 min-h-12 text-[15px] text-left hover:bg-raised',
      tone === 'danger' ? 'text-rose-300' : 'text-fg',
    )}
    data-testid={testid}
  >
    <Icon size={18} className="shrink-0 text-muted" aria-hidden />
    <span className="flex-1 truncate">{label}</span>
  </button>
)

/**
 * The sheet for a held app. `from` says where it was held: the home screens
 * offer to take the icon off, the launcher to put it on or hide it.
 */
export const AppMenu = ({
  app,
  layout,
  from,
  onClose,
}: {
  app: NavItem | null
  layout: PhoneLayout
  from: 'home' | 'launcher'
  onClose: () => void
}) => {
  const actions = useQuickActions(app)
  const setEditing = useEditing((s) => s.set)
  const close = () => onClose()
  const run = (fn: () => void | Promise<unknown>) => () => {
    close()
    useLauncher.getState().setOpen(false)
    void Promise.resolve(fn()).catch((e: Error) =>
      useUi.getState().showFlash(e.message || 'That did not work', 4000),
    )
  }
  if (!app) return null
  const home = onHome(layout, app.key)
  return (
    <Sheet open={!!app} onClose={close} title={app.label}>
      <div className="flex flex-col gap-3" data-testid="app-menu">
        <div className="flex items-center gap-3 px-1">
          <AppTile app={app} tint={layout.icons} />
          <span className="text-xs text-faint">
            Hold an app for this; hold longer on the home screen to arrange.
          </span>
        </div>
        {actions.length > 0 && (
          <div className="group-list">
            {actions.map((a) => (
              <Row key={a.label} Icon={a.Icon} label={a.label} onClick={run(a.run)} />
            ))}
          </div>
        )}
        <div className="group-list">
          {from === 'launcher' && !home && (
            <Row
              Icon={SquarePlus}
              label="Add to Home Screen"
              onClick={() => {
                close()
                void savePhone(addItem(layout, { k: newKey(), t: 'app', id: app.key }))
                useUi.getState().showFlash(`${app.label} is on your last home screen`, 2500)
              }}
              testid="app-menu-add-home"
            />
          )}
          {home && (
            <Row
              Icon={SquareMinus}
              label="Remove from Home Screen"
              onClick={() => {
                close()
                void savePhone(removeApp(layout, app.key))
              }}
              testid="app-menu-remove-home"
            />
          )}
          <Row
            Icon={LayoutGrid}
            label={from === 'home' ? 'Edit Home Screen' : 'Arrange apps'}
            onClick={() => {
              close()
              setEditing(from === 'home' ? { home: true } : { launcher: true })
            }}
            testid="app-menu-edit"
          />
          {app.key !== 'settings' && app.key !== 'home' && (
            <Row
              Icon={EyeOff}
              label="Hide app"
              tone="danger"
              onClick={() => {
                close()
                void savePhone(hideApp(layout, app.key))
                useUi
                  .getState()
                  .showFlash(`${app.label} is hidden. Settings → Home screen shows it again.`, 3500)
              }}
              testid="app-menu-hide"
            />
          )}
        </div>
      </div>
    </Sheet>
  )
}
